import type { FC } from 'react';

import type { CampusListItem } from '@/types/campus.types';

import CampusStatusPill from './CampusStatusPill';

interface CampusCardProps {
  campus: CampusListItem;
  togglingStatusId: string | null;
  onEdit: (campus: CampusListItem) => void;
  onDelete: (campus: CampusListItem) => void;
  onToggleStatus: (campus: CampusListItem) => void;
}

const MAX_VISIBLE_DOMAINS = 3;

interface StatCellProps {
  label: string;
  value: string | number;
  color: string;
}

const StatCell: FC<StatCellProps> = ({ label, value, color }) => (
  <div className="flex flex-col gap-0.5">
    <span className={`text-[18px] font-extrabold leading-none ${color}`}>{value}</span>
    <span className="mt-1 text-[10.5px] text-[var(--color-ink-muted)]">{label}</span>
  </div>
);

const CampusCard: FC<CampusCardProps> = ({ campus, togglingStatusId, onEdit, onDelete, onToggleStatus }) => {
  const location = [campus.city, campus.state].filter(Boolean).join(', ');
  const visibleDomains = campus.emailDomains.slice(0, MAX_VISIBLE_DOMAINS);
  const extraDomainCount = campus.emailDomains.length - MAX_VISIBLE_DOMAINS;
  const canDelete = campus.userCount === 0;
  const isToggling = togglingStatusId === campus.id;

  return (
    <div className="flex flex-col overflow-hidden rounded-[var(--radius,14px)] border border-[var(--color-divider)] bg-white p-5 shadow-[var(--shadow-card)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_4px_24px_rgba(42,102,217,0.10)]">
      {/* Status pill */}
      <div className="mb-2">
        <CampusStatusPill status={campus.status} />
      </div>

      {/* Name + location */}
      <h3 className="text-[15px] font-extrabold leading-tight text-[var(--color-ink)]">
        {campus.name}
      </h3>
      {location ? (
        <p className="mb-3.5 mt-0.5 text-[12px] text-[var(--color-ink-muted)]">{location}</p>
      ) : (
        <div className="mb-3.5" />
      )}

      {/* Email domains */}
      {campus.emailDomains.length > 0 ? (
        <div className="mb-3.5 flex flex-wrap items-center gap-1">
          {visibleDomains.map((domain) => (
            <span
              key={domain}
              className="rounded-md bg-[var(--color-surface-muted)] px-2 py-0.5 text-[10px] font-medium text-[var(--color-ink-muted)]"
            >
              @{domain}
            </span>
          ))}
          {extraDomainCount > 0 ? (
            <span className="text-[10px] font-medium text-[var(--color-ink-muted)]">
              +{extraDomainCount} more
            </span>
          ) : null}
          {campus.departments.length > 0 ? (
            <span className="rounded-md bg-brand-50 px-2 py-0.5 text-[10px] font-medium text-brand-600">
              {campus.departments.length} dept{campus.departments.length === 1 ? '' : 's'}
            </span>
          ) : null}
        </div>
      ) : null}

      {/* 2×2 stats grid — matches reference layout */}
      <div className="grid grid-cols-2 gap-2.5">
        <StatCell label="Users" value={campus.userCount.toLocaleString()} color="text-brand-500" />
        <StatCell label="Active Rentals" value="—" color="text-[var(--color-success-ink)]" />
        <StatCell label="GMV" value="—" color="text-[var(--color-info-ink)]" />
        <StatCell label="Activity" value="—" color="text-[var(--color-pink-ink)]" />
      </div>

      {/* Activity bar */}
      <div className="mt-3.5 h-[4px] overflow-hidden rounded-full bg-[var(--color-divider)]">
        <div
          className="h-full rounded-full bg-gradient-to-r from-brand-500 to-brand-300"
          style={{ width: '0%' }}
        />
      </div>

      {/* Actions */}
      <div className="mt-4 flex items-center gap-2">
        <button
          type="button"
          onClick={() => onEdit(campus)}
          className="flex-1 rounded-lg border border-[var(--color-divider)] bg-[var(--color-surface-muted)] py-1.5 text-[11.5px] font-semibold text-[var(--color-ink-muted)] transition-colors hover:border-brand-400 hover:bg-brand-50 hover:text-brand-600"
        >
          Edit
        </button>

        <button
          type="button"
          onClick={() => onToggleStatus(campus)}
          disabled={isToggling}
          className={`flex-1 rounded-lg border py-1.5 text-[11.5px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
            campus.status === 'active'
              ? 'border-[var(--color-warn-bg)] bg-[var(--color-warn-bg)] text-[var(--color-warn-ink)] hover:opacity-80'
              : 'border-[var(--color-success-bg)] bg-[var(--color-success-bg)] text-[var(--color-success-ink)] hover:opacity-80'
          }`}
        >
          {isToggling ? '…' : campus.status === 'active' ? 'Pause' : 'Activate'}
        </button>

        <button
          type="button"
          onClick={() => onDelete(campus)}
          disabled={!canDelete}
          title={
            !canDelete
              ? `Cannot delete — ${campus.userCount} user${campus.userCount === 1 ? '' : 's'} registered`
              : 'Delete campus'
          }
          className="flex-1 rounded-lg border border-[var(--color-divider)] bg-[var(--color-surface-muted)] py-1.5 text-[11.5px] font-semibold text-[var(--color-danger-ink)] transition-colors hover:border-[var(--color-danger-ink)] hover:bg-[var(--color-danger-bg)] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-[var(--color-divider)] disabled:hover:bg-[var(--color-surface-muted)]"
        >
          Delete
        </button>
      </div>
    </div>
  );
};

export default CampusCard;
